import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { SearchableEntity } from "../services/searchService";
import { commonTextFieldProps } from "../utils/styles";
import { Clear } from "@mui/icons-material";

interface SearchableAutocompleteProps<T> {
  entityType: SearchableEntity;
  value: T | string | null;
  onSelect: (option: T | null) => void;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  getOptionLabel: (option: T | string) => string;
  renderOptionContent?: (option: T) => React.ReactNode;
  noOptionsText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  fontSize?: string;
  filters?: any;
}

const SearchableAutocomplete = <T extends Record<string, any>>({
  entityType,
  value,
  onSelect,
  onChange,
  label,
  placeholder,
  getOptionLabel,
  renderOptionContent,
  noOptionsText = "No options found",
  disabled = false,
  size = "medium",
  fontSize = "0.75rem",
  filters,
}: SearchableAutocompleteProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: options = [], isLoading: loading } = useSearchQuery(
    entityType as SearchableEntity,
    searchTerm,
    filters || {}
  );

  const debouncedSearch = useCallback(
    debounce((term: string) => setSearchTerm(term), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const defaultRenderOptionContent = (option: T) => (
    <>
      <Typography variant="body1" sx={{ fontSize: fontSize }}>
        {option?.name ||
          option?.petrolPumpName ||
          option?.partyName ||
          option?.clientName}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: fontSize }}
      >
        {option.location && `${option.location} -${" "}`}
        {option.contactNumber ||
          option.phoneNumber ||
          option.primaryContactNumber}
      </Typography>
    </>
  );

  return (
    <Autocomplete<T | string>
      options={options}
      getOptionLabel={getOptionLabel}
      loading={loading}
      value={value}
      onChange={(_, newValue) => {
        if (typeof newValue === "string") {
          onChange(newValue);
          debouncedSearch(newValue);
        } else if (newValue) {
          onSelect(newValue as T);
        } else {
          onSelect(null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          onChange(newInputValue);
          debouncedSearch(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            title: value ? getOptionLabel(value as T) : "Search for " + label,
            ...params.inputProps,
          }}
          placeholder={placeholder}
          sx={{
            ...commonTextFieldProps.sx,
            fontSize: fontSize,
            "& .MuiAutocomplete-endAdornment": {
              right: "4px",
            },
            "& .MuiInputBase-root": {
              paddingRight: "28px !important",
              "&:hover": {
                paddingRight: "52px !important",
              },
              "&.Mui-focused": {
                paddingRight: "52px !important",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: fontSize,
              height: "17px",
              paddingRight: "0px",
              transition: "padding-right 0.2s ease",
            },
          }}
          size={size}
        />
      )}
      clearIcon={<Clear sx={{ fontSize: "0.85rem" }} />}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box fontSize={fontSize} sx={{ width: "100%" }}>
              {renderOptionContent
                ? renderOptionContent(option as T)
                : defaultRenderOptionContent(option as T)}
            </Box>
          </li>
        );
      }}
      noOptionsText={noOptionsText}
      disabled={disabled}
    />
  );
};

export default SearchableAutocomplete;
